/**
 * Mordeczka.eu Admin - Directors Page with nationality and birth year
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
  Switch,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { directorsApi, nationalitiesApi } from "../services/api";
import type { Director, DirectorCreate, DirectorUpdate } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;
const { TextArea } = Input;

export function DirectorsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Director | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 1000 });
  const [showOnlyWithMovies, setShowOnlyWithMovies] = useState(false);
  const { canEdit, canDelete } = usePermissions();

  // Fetch directors
  const { data, isLoading } = useQuery({
    queryKey: ["directors", pagination, showOnlyWithMovies],
    queryFn: () =>
      directorsApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
        has_movies: showOnlyWithMovies || undefined,
      }),
  });

  // Fetch nationalities for select
  const { data: nationalities } = useQuery({
    queryKey: ["nationalities-all"],
    queryFn: () => nationalitiesApi.list({ page_size: 100 }),
  });

  const createMutation = useMutation({
    mutationFn: (data: DirectorCreate) => directorsApi.create(data),
    onSuccess: () => {
      message.success("Reżyser utworzony pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["directors"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć reżysera"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DirectorUpdate }) =>
      directorsApi.update(id, data),
    onSuccess: () => {
      message.success("Reżyser zaktualizowany pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["directors"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować reżysera"),
  });

  const deleteMutation = useMutation({
    mutationFn: directorsApi.delete,
    onSuccess: () => {
      message.success("Reżyser usunięty pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["directors"] });
    },
    onError: () => message.error("Nie udało się usunąć reżysera"),
  });

  const openModal = (item?: Director) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      name: item?.name || "",
      bio: item?.bio || "",
      birth_year: item?.birth_year || null,
      death_year: item?.death_year || null,
      nationality_id: item?.nationality_id || null,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // Validation failed
    }
  };

  const columns: ColumnsType<Director> = [
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name, "pl"),
      defaultSortOrder: "ascend",
    },
    {
      title: "Liczba dzieł",
      dataIndex: "works_count",
      key: "works_count",
      width: 120,
      align: "center",
      render: (count) => count || 0,
      sorter: (a, b) => (a.works_count || 0) - (b.works_count || 0),
    },
    {
      title: "Narodowość",
      key: "nationality",
      width: 180,
      render: (_, record) => {
        const nationality = record.nationality;
        if (!nationality) return "—";
        return (
          <Space>
            {nationality.flag_url && (
              <img
                src={nationality.flag_url}
                alt={`Flaga ${nationality.name}`}
                style={{ width: 24, height: 16, objectFit: "cover", borderRadius: 2 }}
              />
            )}
            <span>{nationality.name}</span>
          </Space>
        );
      },
      sorter: (a, b) =>
        (a.nationality?.name || "").localeCompare(b.nationality?.name || "", "pl"),
    },
    {
      title: "Rok urodzenia",
      dataIndex: "birth_year",
      key: "birth_year",
      width: 130,
      render: (year) => year || "—",
      sorter: (a, b) => (a.birth_year || 0) - (b.birth_year || 0),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: Director) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć tego reżysera?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            okButtonProps={{ danger: true }}
            cancelText="Anuluj"
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Reżyserzy
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nowego
        </Button>
      </div>

      <Card>
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <Switch
            id="movies-filter"
            checked={showOnlyWithMovies}
            onChange={setShowOnlyWithMovies}
          />
          <label htmlFor="movies-filter" style={{ cursor: "pointer" }}>
            Pokaż tylko reżyserów z jakimkolwiek filmem
          </label>
        </div>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["1000", "2000", "5000"],
            showTotal: (total) => `Łącznie ${total} reżyserów`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj reżysera" : "Dodaj reżysera"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Imię i nazwisko"
            rules={[{ required: true, message: "Proszę wprowadzić nazwę" }]}
          >
            <Input placeholder="np. Steven Spielberg" />
          </Form.Item>

          <Form.Item name="nationality_id" label="Narodowość">
            <Select
              placeholder="Wybierz narodowość"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {nationalities?.items.map((nat) => (
                <Select.Option key={nat.id} value={nat.id}>
                  {nat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Space size="large" style={{ width: "100%" }}>
            <Form.Item
              name="birth_year"
              label="Rok urodzenia"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                placeholder="np. 1946"
                min={-3000}
                max={new Date().getFullYear()}
                style={{ width: 150 }}
              />
            </Form.Item>

            <Form.Item
              name="death_year"
              label="Rok śmierci"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                placeholder="(opcjonalnie)"
                min={-3000}
                max={new Date().getFullYear()}
                style={{ width: 150 }}
              />
            </Form.Item>
          </Space>

          <Form.Item name="bio" label="Biografia" style={{ marginTop: 16 }}>
            <TextArea rows={3} placeholder="Krótka biografia reżysera..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default DirectorsPage;
